import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { useLoaderData, useLocation } from 'react-router';
import { CxFooter, HeaderV2, shouldUseUkCdnBetslip, UKBetslip } from '@cx-web/components/composites';
import Logo from '@cx-web/components/primitives/Logo';
import { type FooterV2Response } from '@cx-web/domain-types';
import { logout } from '@cx-web/feature-auth';
import MiniBetslipWithContextAndStoreSlices from '@cx-web/feature-betslip/components/components/mini-betslip';
import { CookieService } from '@cx-web/feature-cookies';
import { desktopNewHeaderStyleFlagName, resolveDesktopNewHeaderStyle } from '@cx-web/feature-flags';
import { featureFlags, useFeatureFlag, useFeatureFlagByName } from '@cx-web/feature-flags/provider';
import { type AdUnitConfiguration } from '@cx-web/feature-gam-advertising';
import { getDefaultState, getStateName, isValidState, type StateCode } from '@cx-web/feature-geolocation';
import { useGeolocationContext } from '@cx-web/feature-geolocation/context';
import { useLabelActions } from '@cx-web/feature-i18n';
import { useMyOCAuth } from '@cx-web/feature-myoc';
import { useAuthModal, useOptimisticLoginState, VIEW_LOGIN, VIEW_SIGNUP } from '@cx-web/feature-myoc-login/provider';
import { changeOddsFormat } from '@cx-web/feature-odds-format';
import { useRepubContext } from '@cx-web/feature-repub';
import { useSiteConfig } from '@cx-web/feature-site-config';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
  TRACKING_LABELS,
  trackingActions,
} from '@cx-web/feature-tracking';
import { buildDesktopHeaderTrackingEvent } from '@cx-web/feature-tracking/desktop-header';
import { useNavData } from '@cx-web/ui-shell/nav-data-context';
import { useUIConfig } from '@cx-web/ui-shell/ui-config-context';
import { getRepubHomeUrl, getRepubMetaFromRepub, isUkRepub } from '@cx-web/utils';
import { useCmpProps } from '@cx-web/utils/cmp';
import { trackEvent } from '@cx-web/utils/dataLayer';

import AdSlot from '~/components/composites/ads/AdSlot';
import GoogleAdUnit from '~/components/composites/ads/GoogleAdUnit';
import MiniMyOC from '~/components/composites/MiniMyOC';
import { ONE_DAY } from '~/constants';
import { usePageContext } from '~/providers/page-context';
import MobileBottomNav from '~/routes/$/components/MobileBottomNav';
import { type OddsFormat } from '~/types';

export const LayoutComponents = ({
  adUnitConfiguration,
  children,
  footerData,
  userOddsFormat,
}: PropsWithChildren<{
  adUnitConfiguration: AdUnitConfiguration[];
  footerData?: FooterV2Response;
  userOddsFormat: OddsFormat;
}>) => {
  const { myocConfig, myocData } = useMyOCAuth();
  const { openAtView } = useAuthModal();
  const { state } = useGeolocationContext();
  const labelActions = useLabelActions();
  const { repub } = useRepubContext();
  const siteConfig = useSiteConfig();
  const loaderData = useLoaderData<{ url: string }>();
  const { pathname } = useLocation();
  const { isAdmiralCookieBannerEnabled, resourcesUrl } = useUIConfig();
  const navData = useNavData();
  const cmpProps = useCmpProps();
  // ProsperStack portal URL from the curated, client-serialised env (PageContext), not the bare global
  // ENV — guarantees the value is the same on SSR and after hydration.
  const { ENV } = usePageContext();

  const cookieService = useMemo(() => new CookieService(), []);

  const stateOptions = useMemo(() => {
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    return flattenedCodes
      .map(code => ({ code, label: getStateName(code as StateCode) ?? code }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [siteConfig?.supportedStates]);

  const selectedState = useMemo(() => {
    const stateCode = state as StateCode;
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    if (isValidState(stateCode) && flattenedCodes.includes(stateCode)) return stateCode;
    return siteConfig?.statesAssignments?.[stateCode] ?? getDefaultState(stateCode) ?? '';
  }, [state, siteConfig?.supportedStates, siteConfig?.statesAssignments]);

  // Optimistic login state: show the logged-in header immediately after the myoc widget reports
  // success, without waiting for a page reload to refresh myocData from the server. Driven by the
  // framework-agnostic auth-modal store (NGF-6331) instead of listening to OC:* window events: the
  // legacy bridge translates the OC widget's events into store writes. Shared with atlas-astro's
  // CxHeaderWrapper via useOptimisticLoginState so the two headers can't drift.
  const isOCLoggedIn = useOptimisticLoginState(!!myocData?.myOCUser);

  // Gated on RENDER_UK_HEADER_STYLE (NGF-6475) and this repub's own flag (OCGC-3417) — see
  // resolveDesktopNewHeaderStyle. Default OFF — fail-open to legacy header.
  // When ON: UK-style header renders (ProfileDropdown/LoginSignupButtons, new OddsFormat/StateDropdown).
  // When OFF (default): pre-6cb4313d legacy header (legacy account menu, OddsFormatDropdownLegacy,
  // original DesktopConfigItems gap + SettingsDropdown icon).
  const renderUkHeaderStyle = useFeatureFlag(featureFlags.RENDER_UK_HEADER_STYLE) ?? false;
  const desktopNewHeaderStyleForRepub = useFeatureFlagByName(desktopNewHeaderStyleFlagName(repub));
  const isUkStyle = resolveDesktopNewHeaderStyle(renderUkHeaderStyle, desktopNewHeaderStyleForRepub);

  const handleLogout = useCallback(() => {
    trackEvent(TRACKING_EVENTS.LOG_OUT, {
      eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.LOG_OUT],
    });
    logout(repub!, getRepubHomeUrl(repub!));
  }, [repub]);

  // UK sits above the nav (its long-standing position); every other ad-eligible repub sits below —
  // moving it here is a pure DOM-order swap around <HeaderV2>, no separate implementation needed.
  const leaderboard = <AdSlot adUnitConfiguration={adUnitConfiguration} placement="leaderboard" />;
  const isUk = isUkRepub(repub);

  return (
    <>
      <GoogleAdUnit adUnitConfiguration={adUnitConfiguration} />
      {isUk && leaderboard}

      <HeaderV2
        betslipSlot={
          shouldUseUkCdnBetslip(isUkStyle, repub) ? (
            <UKBetslip
              apiKey={ENV.OC_API_KEY[repub]}
              betslipScriptUrl={ENV.BETSLIP_SCRIPT_URL}
              deviceId={myocConfig.deviceID}
              oddsFormat={userOddsFormat}
              repub={repub}
            />
          ) : (
            <MiniBetslipWithContextAndStoreSlices
              betslipTitle={labelActions?.get('RESPONSIVE_BETSLIP') ?? ''}
              variant={isUkStyle ? 'icon' : 'pill'}
            />
          )
        }
        isLoggedIn={isOCLoggedIn}
        isUkStyle={isUkStyle}
        logoutLabel={labelActions?.get('RESPONSIVE_LOG_OUT') ?? 'Log Out'}
        navData={navData}
        navigationKey={pathname}
        oddsFormat={userOddsFormat}
        onDesktopHeaderItemClick={({ level, selection }) => {
          trackingActions.eventTracking(buildDesktopHeaderTrackingEvent({ level, selection }));
        }}
        onLogout={handleLogout}
        onMenuToggle={() => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_CLICK,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
            eventLabel: TRACKING_LABELS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
          });
        }}
        onNavItemClick={({ label }) => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_ITEM,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_ITEM],
            eventLabel: label,
          });
        }}
        onOddsFormatChange={changeOddsFormat}
        onStateChange={newState => {
          cookieService.select('Subdivision-Code').set(newState, new Date().getTime() + ONE_DAY);
          cookieService.select('us_state').set(newState, new Date().getTime() + ONE_DAY);
          window.location.reload();
        }}
        renderAccountMenu={variant => (
          <MiniMyOC
            isLoggedIn={isOCLoggedIn}
            isUkStyle={isUkStyle}
            labelActions={labelActions}
            onLogout={handleLogout}
            onSignIn={() => openAtView(VIEW_LOGIN)}
            onSignUp={() => openAtView(VIEW_SIGNUP)}
            prosperStackUrl={ENV.PROSPERSTACK_URL}
            repub={repub}
            showProfileLink={repub === 'US'}
            siteConfig={siteConfig}
            type={variant === 'desktop' ? 'desktop' : 'burgerTop'}
          />
        )}
        renderLogo={<Logo resourcesUrl={resourcesUrl} />}
        repub={repub}
        repubHomeUrl={getRepubHomeUrl(repub!)}
        selectedState={selectedState}
        serverUrl={loaderData.url}
        siteConfig={siteConfig}
        stateOptions={stateOptions}
      />
      {!isUk && leaderboard}
      {children}
      <CxFooter
        cmpProps={cmpProps}
        footerData={footerData}
        isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
        resourcesUrl={resourcesUrl}
      />
      <MobileBottomNav isIT={getRepubMetaFromRepub(repub!).repubIs.IT} pathname={pathname} serverUrl={loaderData.url} />
      <AdSlot adUnitConfiguration={adUnitConfiguration} placement="mobile-sticky-bottom" />
    </>
  );
};
